// @ts-strict-ignore
import { useIntl } from 'react-intl';

import { Ratio, Text } from 'components/@tedui';

import { AdCompanion } from 'components/ads';
import ContentContainer from 'components/content-container';
import Grid from 'components/grid';
import Col from 'components/grid/col';
import IconCTAWatch from 'components/icon-cta-watch';
import Markdown from 'components/markdown';

import { generateAdToken } from 'lib/business';
import getImageFromSet from 'lib/get-image-from-set';
import { usePageTargeting } from 'lib/ads/usePageTargeting';

import SeriesPageHeaderProps from './SeriesPageHeader.props';

const seriesTitlesThatHideEyebrow = 'The Audacious Project';

const SeriesPageHeader = ({
  title,
  description,
  seriesSlug,
  imageSet,
  watchNowLink
}: SeriesPageHeaderProps) => {
  const intl = useIntl();
  const eyebrowText = intl.formatMessage({ defaultMessage: 'Series' });

  const imageSquare = getImageFromSet(imageSet, '1x1');
  const imageSmallRect = getImageFromSet(imageSet, '400x209');
  const imageLargeRect = getImageFromSet(imageSet, '10x3');

  const AD_TOKEN = generateAdToken();

  // Set page-level targeting for this series
  usePageTargeting({ series: seriesSlug });

  const imageElement = (ratio, image) => {
    return (
      <Ratio ratio={ratio} isAriaHidden>
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          className="size-full bg-gray-300 bg-contain bg-center"
          alt={title}
          src={image}
        />
      </Ratio>
    );
  };

  return (
    <div className="w-full">
      <div className="md:hidden">{imageElement([1, 1], imageSquare)}</div>
      <div className="hidden md:block lg:hidden">
        {imageElement([400, 209], imageSmallRect)}
      </div>
      <div className="hidden lg:block xl:hidden">
        {imageElement([10, 3], imageLargeRect)}
      </div>
      <ContentContainer>
        <div className="hidden xl:block">
          {imageElement([10, 3], imageLargeRect)}
        </div>
      </ContentContainer>
      <ContentContainer className="w-full bg-white py-5 md:pt-10 xl:pt-16">
        <Grid>
          <Col sm={4} md={6} lg={6} xl={5}>
            {title !== seriesTitlesThatHideEyebrow && (
              <Text
                tag="div"
                isBold
                variant="caption"
                UNSAFE_className="uppercase text-gray-500"
                testId="series-page-header-caption"
              >
                {eyebrowText}
              </Text>
            )}
            <Text
              tag="h3"
              isBold
              variant="header1"
              UNSAFE_className="mt-2 mb-3"
            >
              {title}
            </Text>
            <Text tag="div" UNSAFE_className="text-gray-700">
              <Markdown>{description}</Markdown>
            </Text>
            {watchNowLink && (
              <IconCTAWatch
                link={watchNowLink}
                className="mb-8 mt-8 md:mb-0 md:mt-12"
              />
            )}
          </Col>
          <Col
            sm={4}
            md={6}
            lg={6}
            xl={5}
            offsetXl={2}
            className="flex justify-start pb-4 md:justify-end"
          >
            <AdCompanion
              path="/ted3/web/series"
              videoAd={{
                token: AD_TOKEN,
                custParams: { series: seriesSlug }
              }}
              isVideoAd={false}
            />
          </Col>
        </Grid>
      </ContentContainer>
    </div>
  );
};

export default SeriesPageHeader;
